import type { GuideTourStepPosition } from '../types'
import type { SpotlightBounds } from '../overlay/utils'

export {
  GUIDE_TOUR_STEP_CARD_WIDTH,
  GUIDE_TOUR_STEP_CARD_WIDTH_BY_SIZE,
  getGuideTourStepCardEstimatedHeight,
  getGuideTourStepCardWidth,
  resolveGuideTourStepCardSize,
} from './stepCardSize'

export const GUIDE_TOUR_STEP_CARD_GAP = 16
export const GUIDE_TOUR_STEP_CARD_VIEWPORT_PADDING = 16

export type StepCardPositionInput = {
  bounds: SpotlightBounds
  position: GuideTourStepPosition
  cardWidth: number
  cardHeight: number
  viewportWidth: number
  viewportHeight: number
  gap?: number
  viewportPadding?: number
}

export type StepCardPosition = {
  top: number
  left: number
}

type ResolvedSide = Exclude<GuideTourStepPosition, 'auto'>

const POSITION_FALLBACK_ORDER: readonly ResolvedSide[] = [
  'right',
  'bottom',
  'top',
  'left',
]

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max)

const clampPosition = ({
  top,
  left,
  cardWidth,
  cardHeight,
  viewportWidth,
  viewportHeight,
  viewportPadding,
}: StepCardPosition & {
  cardWidth: number
  cardHeight: number
  viewportWidth: number
  viewportHeight: number
  viewportPadding: number
}): StepCardPosition => {
  const maxLeft = viewportWidth - cardWidth - viewportPadding
  const maxTop = viewportHeight - cardHeight - viewportPadding

  return {
    top: clamp(top, viewportPadding, maxTop),
    left: clamp(left, viewportPadding, maxLeft),
  }
}

const getCardPositionForSide = (
  side: ResolvedSide,
  {
    bounds,
    cardWidth,
    cardHeight,
    gap = GUIDE_TOUR_STEP_CARD_GAP,
  }: Pick<StepCardPositionInput, 'bounds' | 'cardWidth' | 'cardHeight' | 'gap'>
): StepCardPosition => {
  const centeredLeft = bounds.left + bounds.width / 2 - cardWidth / 2
  const centeredTop = bounds.top + bounds.height / 2 - cardHeight / 2

  switch (side) {
    case 'bottom':
      return {
        top: bounds.top + bounds.height + gap,
        left: centeredLeft,
      }
    case 'top':
      return {
        top: bounds.top - cardHeight - gap,
        left: centeredLeft,
      }
    case 'left':
      return {
        top: centeredTop,
        left: bounds.left - cardWidth - gap,
      }
    case 'right':
      return {
        top: centeredTop,
        left: bounds.left + bounds.width + gap,
      }
  }
}

const getVisibleCardArea = (
  position: StepCardPosition,
  cardWidth: number,
  cardHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  viewportPadding: number
): number => {
  const visibleLeft = Math.max(position.left, viewportPadding)
  const visibleTop = Math.max(position.top, viewportPadding)
  const visibleRight = Math.min(
    position.left + cardWidth,
    viewportWidth - viewportPadding
  )
  const visibleBottom = Math.min(
    position.top + cardHeight,
    viewportHeight - viewportPadding
  )

  return (
    Math.max(0, visibleRight - visibleLeft) *
    Math.max(0, visibleBottom - visibleTop)
  )
}

const fitsInsideViewport = (
  position: StepCardPosition,
  cardWidth: number,
  cardHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  viewportPadding: number
): boolean => {
  const cardArea = cardWidth * cardHeight
  const visibleArea = getVisibleCardArea(
    position,
    cardWidth,
    cardHeight,
    viewportWidth,
    viewportHeight,
    viewportPadding
  )

  return visibleArea >= cardArea
}

const resolveSide = ({
  bounds,
  position,
  cardWidth,
  cardHeight,
  viewportWidth,
  viewportHeight,
  gap = GUIDE_TOUR_STEP_CARD_GAP,
  viewportPadding = GUIDE_TOUR_STEP_CARD_VIEWPORT_PADDING,
}: StepCardPositionInput): ResolvedSide => {
  const preferred: ResolvedSide = position === 'auto' ? 'right' : position

  const preferredPosition = getCardPositionForSide(preferred, {
    bounds,
    cardWidth,
    cardHeight,
    gap,
  })

  if (
    fitsInsideViewport(
      preferredPosition,
      cardWidth,
      cardHeight,
      viewportWidth,
      viewportHeight,
      viewportPadding
    )
  ) {
    return preferred
  }

  const sideOptions = POSITION_FALLBACK_ORDER.filter(side => side !== preferred)

  const ranked = sideOptions
    .map(option => {
      const cardPosition = getCardPositionForSide(option, {
        bounds,
        cardWidth,
        cardHeight,
        gap,
      })

      return {
        option,
        position: cardPosition,
        visibleArea: getVisibleCardArea(
          cardPosition,
          cardWidth,
          cardHeight,
          viewportWidth,
          viewportHeight,
          viewportPadding
        ),
        fits: fitsInsideViewport(
          cardPosition,
          cardWidth,
          cardHeight,
          viewportWidth,
          viewportHeight,
          viewportPadding
        ),
      }
    })
    .sort((left, right) => {
      if (left.fits !== right.fits) {
        return Number(right.fits) - Number(left.fits)
      }

      return right.visibleArea - left.visibleArea
    })

  return ranked[0]?.option ?? preferred
}

export const getScreenStepCardPosition = ({
  cardWidth,
  cardHeight,
  viewportWidth,
  viewportHeight,
  viewportPadding = GUIDE_TOUR_STEP_CARD_VIEWPORT_PADDING,
}: {
  cardWidth: number
  cardHeight: number
  viewportWidth: number
  viewportHeight: number
  viewportPadding?: number
}): StepCardPosition =>
  clampPosition({
    top: viewportHeight / 2 - cardHeight / 2,
    left: viewportWidth / 2 - cardWidth / 2,
    cardWidth,
    cardHeight,
    viewportWidth,
    viewportHeight,
    viewportPadding,
  })

export type GuideTourStepCardArrowSide = 'top' | 'right' | 'bottom' | 'left'

export type GuideTourStepCardLayout = StepCardPosition & {
  arrowSide: GuideTourStepCardArrowSide | null
}

const mapSideToArrowSide = (side: ResolvedSide): GuideTourStepCardArrowSide => {
  switch (side) {
    case 'right':
      return 'left'
    case 'left':
      return 'right'
    case 'bottom':
      return 'top'
    case 'top':
      return 'bottom'
  }
}

export const getStepCardLayout = (
  input: StepCardPositionInput
): GuideTourStepCardLayout => {
  const {
    bounds,
    cardWidth,
    cardHeight,
    viewportWidth,
    viewportHeight,
    gap = GUIDE_TOUR_STEP_CARD_GAP,
    viewportPadding = GUIDE_TOUR_STEP_CARD_VIEWPORT_PADDING,
  } = input

  const resolvedSide = resolveSide(input)
  const position = getCardPositionForSide(resolvedSide, {
    bounds,
    cardWidth,
    cardHeight,
    gap,
  })

  return {
    ...clampPosition({
      ...position,
      cardWidth,
      cardHeight,
      viewportWidth,
      viewportHeight,
      viewportPadding,
    }),
    arrowSide: mapSideToArrowSide(resolvedSide),
  }
}
